import { useContext, useEffect, useState } from 'react'
import { AuthContext } from 'contexts/AuthContext'

import { Wrapper } from 'components'
import { AppContext } from 'contexts/AppContext'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  Card,
  Input,
  toast,
  Spinner,
  TableCell,
  Button,
  Label,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  Popover,
  PopoverAction,
  PopoverContent,
  DatePicker,
} from 'keep-react'
import { format } from 'date-fns'
import {
  ArrowDown,
  ArrowsDownUp,
  ArrowUp,
  Calendar,
  MagnifyingGlass,
  MicrosoftExcelLogo,
} from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'
import { InvoiceListResponse, InvoiceType } from 'api/types/invoice'
import { invoiceService } from 'api/services/invoice'
import { formatCurrency } from 'utils/currency'

interface QueryVars {
  brokerNo: number
  receiptNo?: string
  policyNo?: string
  nif?: string
  product?: string
  stage?: 'O' | 'P'
  invoicedFrom?: string
  invoicedTo?: string
  page: number
}

type SortProp =
  | 'Apólice'
  | 'Tomador'
  | 'Recibo nº'
  | 'Data de Emissão'
  | 'Data de Cobrança'
  | 'Produto'
  | 'Prémio Total'
  | 'Comissão'
  | 'Periodo'
  | 'Pago por'
  | 'Pago'

export const Invoices = () => {
  const navigate = useNavigate()
  const { products } = useContext(AppContext)

  const { currentBroker } = useContext(AuthContext)
  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0
  const [data, setData] = useState<InvoiceType[] | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [sortProp, setSortProp] = useState<SortProp | null>(null)
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const policy = params.get('policyNo')

  const [queryVars, setQueryVars] = useState<QueryVars>({
    brokerNo: brokerNo,
    receiptNo: undefined,
    policyNo: policy ? policy : undefined,
    nif: undefined,
    product: undefined,
    stage: undefined,
    invoicedFrom: undefined,
    invoicedTo: undefined,
    page: 0,
  })

  const getData = async () => {
    try {
      setData(null)
      setIsLoading(true)
      const response = (await invoiceService.getInvoiceList({
        ...queryVars,
      })) as InvoiceListResponse

      setIsLoading(false)
      setData(response.Invoices)
      setTotalCount(response.TotalCount)
    } catch (error) {
      toast.error('Erro a pedir o recibo')
    }
  }

  const handleSearch = async () => {
    setIsLoading(true)
    getData()
  }

  const handleSort = (prop: SortProp) => {
    setSortProp(prev => {
      if (prev === prop) {
        if (sortOrder === 'ASC') {
          setSortOrder('DESC')
        } else {
          setSortOrder('ASC')
        }
      } else {
        setSortOrder('ASC')
      }
      return prop
    })
  }

  const handleExcelDownload = async () => {
    try {
      const response = (await invoiceService.getInvoiceList({
        ...queryVars,
        output: 'Excel',
      })) as InvoiceListResponse

      const contentType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

      // decode base64 in chunks (handles large files)
      const byteChars = atob(response.Output || '')
      const sliceSize = 1024
      const byteArrays = []
      for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
        const slice = byteChars.slice(offset, offset + sliceSize)
        const byteNumbers = new Array(slice.length)
        for (let i = 0; i < slice.length; i++)
          byteNumbers[i] = slice.charCodeAt(i)
        byteArrays.push(new Uint8Array(byteNumbers))
      }

      const blob = new Blob(byteArrays, { type: contentType })
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = Date.now().toString() // respected by most modern browsers
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading Excel file:', error)
      toast.error(
        'Erro ao descarregar o ficheiro Excel. Por favor, tente novamente.',
      )
    }
  }

  useEffect(() => {
    setQueryVars(prev => ({ ...prev, brokerNo: brokerNo }))
  }, [brokerNo])

  useEffect(() => {
    getData()
  }, [queryVars.page, queryVars.brokerNo])

  useEffect(() => {
    if (!data || !sortProp) return

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortProp]
      const bValue = b[sortProp]

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }
      return 0
    })

    setData(sorted)
  }, [sortProp, sortOrder])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Consulte os seus recibos
        </h2>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600">
          Recibos
        </h1>
        <Card className="mb-6 p-4 w-full max-w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 items-end">
            <div>
              <Label className="text-body-4 block pb-2">Nº do Recibo</Label>
              <Input
                placeholder="Nº do Recibo"
                onChange={e => {
                  setQueryVars(prev => ({ ...prev, receiptNo: e.target.value }))
                }}
              />
            </div>
            <div>
              <Label className="text-body-4 block pb-2">Apólice</Label>
              <Input
                placeholder="Apólice"
                value={queryVars.policyNo ?? undefined}
                onChange={e => {
                  setQueryVars(prev => ({ ...prev, policyNo: e.target.value }))
                }}
              />
            </div>
            <div>
              <Label className="text-body-4 block pb-2">NIF</Label>
              <Input
                placeholder="NIF"
                onChange={e => {
                  setQueryVars(prev => ({ ...prev, nif: e.target.value }))
                }}
              />
            </div>
            <div>
              <Label className="text-body-4 block pb-2">Produto</Label>
              <Select
                name="product"
                onValueChange={val => {
                  if (val === 'none') {
                    setQueryVars(prev => ({ ...prev, product: undefined }))
                    return
                  }
                  setQueryVars(prev => ({ ...prev, product: val }))
                }}
              >
                <SelectAction className="[&_span]:truncate [&_span]:max-w-[90%]">
                  <SelectValue placeholder="Produto" />
                </SelectAction>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">Todos</SelectItem>
                    {products.map(product => (
                      <SelectItem key={product.product} value={product.product}>
                        {product.description}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-body-4 block pb-2">Data de Emissão</Label>
              <Popover>
                <PopoverAction asChild className="!w-full">
                  <Button
                    color="secondary"
                    size="md"
                    className="w-[286px] justify-start gap-2 border border-metal-100"
                    variant="outline"
                  >
                    <Calendar
                      size={20}
                      className="text-metal-400 dark:text-white"
                    />
                    {queryVars.invoicedFrom ? (
                      format(
                        new Date(queryVars.invoicedFrom) ?? new Date(),
                        'PPP',
                      )
                    ) : (
                      <span>Data de Emissão</span>
                    )}
                  </Button>
                </PopoverAction>
                <PopoverContent align="start" className="max-w-min border-0">
                  <DatePicker
                    mode="single"
                    selected={
                      queryVars.invoicedFrom
                        ? new Date(queryVars.invoicedFrom)
                        : new Date()
                    }
                    onSelect={date =>
                      setQueryVars(prev => ({
                        ...prev,
                        invoicedFrom: date
                          ? format(date, 'yyyy-MM-dd')
                          : undefined,
                      }))
                    }
                    showOutsideDays={true}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-body-4 block pb-2">Data de Cobrança</Label>
              <Popover>
                <PopoverAction asChild className="!w-full">
                  <Button
                    color="secondary"
                    size="md"
                    className="w-[286px] justify-start gap-2 border border-metal-100"
                    variant="outline"
                  >
                    <Calendar
                      size={20}
                      className="text-metal-400 dark:text-white"
                    />
                    {queryVars.invoicedTo ? (
                      format(
                        new Date(queryVars.invoicedTo) ?? new Date(),
                        'PPP',
                      )
                    ) : (
                      <span>Data de Cobrança</span>
                    )}
                  </Button>
                </PopoverAction>
                <PopoverContent align="start" className="max-w-min border-0">
                  <DatePicker
                    mode="single"
                    selected={
                      queryVars.invoicedTo
                        ? new Date(queryVars.invoicedTo)
                        : new Date()
                    }
                    onSelect={date =>
                      setQueryVars(prev => ({
                        ...prev,
                        invoicedTo: date
                          ? format(date, 'yyyy-MM-dd')
                          : undefined,
                      }))
                    }
                    showOutsideDays={true}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-body-4 block pb-2">Estado</Label>
              <Select
                name="stage"
                onValueChange={val => {
                  if (val === 'none') {
                    setQueryVars(prev => ({ ...prev, stage: undefined }))
                    return
                  }
                  setQueryVars(prev => ({ ...prev, stage: val as 'O' | 'P' }))
                }}
              >
                <SelectAction className="[&_span]:truncate [&_span]:max-w-[90%]">
                  <SelectValue placeholder="Todos" />
                </SelectAction>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">Todos</SelectItem>
                    <SelectItem value="P">Pago</SelectItem>
                    <SelectItem value="O">Por liquidar</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <Button
                color="primary"
                variant="default"
                className="h-11 gap-2 text-body-5"
                onClick={() => handleSearch()}
              >
                <MagnifyingGlass size={20} />
                Pesquisar
              </Button>
              <Button
                color="secondary"
                variant="outline"
                className="h-11 gap-2 text-body-5"
                onClick={() => handleExcelDownload()}
              >
                <MicrosoftExcelLogo size={20} />
                Excel
              </Button>
            </div>
          </div>
        </Card>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !data ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Recibo indisponível
            </div>
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap px-3">
                    <div className="flex items-center gap-1">
                      Nº Recibo
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Recibo nº'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Recibo nº')}
                      >
                        {sortProp === 'Recibo nº' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Recibo nº' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Recibo nº' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Apólice
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Apólice'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Apólice')}
                      >
                        {sortProp === 'Apólice' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Apólice' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Apólice' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Tomador
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Tomador'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Tomador')}
                      >
                        {sortProp === 'Tomador' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Tomador' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Tomador' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Produto
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Produto'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Produto')}
                      >
                        {sortProp === 'Produto' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Produto' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Produto' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Data de Emissão
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Data de Emissão'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Data de Emissão')}
                      >
                        {sortProp === 'Data de Emissão' &&
                          sortOrder === 'ASC' && <ArrowUp size={16} />}
                        {sortProp === 'Data de Emissão' &&
                          sortOrder === 'DESC' && <ArrowDown size={16} />}
                        {sortProp !== 'Data de Emissão' && (
                          <ArrowsDownUp size={16} />
                        )}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Período
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Periodo'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Periodo')}
                      >
                        {sortProp === 'Periodo' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Periodo' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Periodo' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Prémio Total
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Prémio Total'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Prémio Total')}
                      >
                        {sortProp === 'Prémio Total' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Prémio Total' &&
                          sortOrder === 'DESC' && <ArrowDown size={16} />}
                        {sortProp !== 'Prémio Total' && (
                          <ArrowsDownUp size={16} />
                        )}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Com. Bruta
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Comissão'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Comissão')}
                      >
                        {sortProp === 'Comissão' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Comissão' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Comissão' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3">
                    <div className="flex items-center gap-1">
                      Estado
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Pago por'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Pago por')}
                      >
                        {sortProp === 'Pago por' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Pago por' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Pago por' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="px-3"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.map((item: InvoiceType, index: number) => (
                  <TableRow
                    key={`${item['Recibo nº']}-${index}`}
                    className="even:bg-primary-50/25"
                  >
                    <TableCell className="px-3">{item['Recibo nº']}</TableCell>
                    <TableCell className="px-3">{item['Apólice']}</TableCell>
                    <TableCell className="px-3">{item['Tomador']}</TableCell>
                    <TableCell className="px-3">{item['Produto']}</TableCell>
                    <TableCell className="px-3 whitespace-nowrap">
                      {item['Data de Emissão']
                        ? format(
                            new Date(item['Data de Emissão']),
                            'dd-MM-yyyy',
                          )
                        : ''}
                    </TableCell>
                    <TableCell className="px-3 whitespace-nowrap normal-case">
                      {item['Periodo']}
                    </TableCell>
                    <TableCell className="px-3 text-right">
                      {formatCurrency(item['Prémio Total'])}
                    </TableCell>
                    <TableCell className="px-3 text-right">
                      {formatCurrency(item['Comissão'])}
                    </TableCell>
                    <TableCell className="px-3 normal-case">
                      {item['Pago'] === 'Pago'
                        ? `Pago por ${item['Pago por'].toLowerCase()}`
                        : 'Por liquidar'}
                    </TableCell>
                    <TableCell className="px-3 text-right">
                      <Button
                        variant="outline"
                        color="secondary"
                        title="Recibos"
                        onClick={() =>
                          navigate(
                            RouteNamesAuth.INVOICE_DETAILS.replace(
                              ':receiptNo',
                              String(item['Recibo nº']),
                            ),
                          )
                        }
                      >
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="pt-10 text-center">
              <nav className="inline-flex justify-center gap-4  bg-white rounded-full">
                <Button
                  variant="softBg"
                  color="secondary"
                  disabled={queryVars.page === 0}
                  className="rounded-r-none"
                  onClick={() =>
                    setQueryVars(prev => ({
                      ...prev,
                      page: prev.page > 0 ? prev.page - 1 : 0,
                    }))
                  }
                >
                  Anterior
                </Button>
                <div className="text-body-4 text-metal-500 flex items-center">
                  Página {queryVars.page + 1}
                </div>
                <Button
                  variant="softBg"
                  color="secondary"
                  disabled={
                    totalCount !== null
                      ? (queryVars.page + 1) * 50 >= totalCount
                      : false
                  }
                  className="rounded-l-none"
                  onClick={() =>
                    setQueryVars(prev => ({
                      ...prev,
                      page: prev.page + 1,
                    }))
                  }
                >
                  Seguinte
                </Button>
              </nav>
            </div>
          </>
        )}
      </Wrapper>
    </div>
  )
}
